<template>
  <div
    :id="`investigation-call-${call.id}`"
    class="investigation-call"
    :class="{ 'investigation-call--failed': failed }"
    :data-qa="`investigation-call-${call.id}`"
  >
    <button
      type="button"
      class="investigation-call__head"
      :aria-expanded="open"
      @click="$emit('toggle')"
    >
      <span class="investigation-call__id">#{{ call.id }}</span>
      <span class="investigation-call__method" :class="methodClass">{{ call.method }}</span>
      <span class="investigation-call__operation">
        <b>{{ call.operation }}</b>
        <span>{{ call.system }} · {{ call.stage }}</span>
      </span>
      <span class="investigation-call__status">
        <span
          class="ds-chip ds-chip--dot"
          :class="failed ? 'ds-chip--danger' : 'ds-chip--positive'"
        >
          {{ statusLabel }}
        </span>
      </span>
      <span class="investigation-call__flags">
        <span v-if="call.truncated" class="ds-chip ds-chip--warning">afgekapt</span>
        <span class="investigation-call__duration">{{ formatDuration(call.durationMs) }}</span>
      </span>
      <svg
        class="investigation-call__chevron"
        :class="{ 'investigation-call__chevron--open': open }"
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 3l4 4-4 4"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div v-if="open" class="investigation-call__body">
      <div class="investigation-call__block">
        <div class="investigation-call__block-head">
          <h6>Endpoint</h6>
          <investigation-copy-button
            :text="call.uri"
            :data-qa="`investigation-call-${call.id}-copy-uri`"
          />
        </div>
        <div class="investigation-call__uri">{{ call.uri }}</div>
      </div>

      <div v-if="call.errorType || call.errorMessage" class="investigation-call__block">
        <div class="investigation-call__block-head"><h6>Fout</h6></div>
        <div class="investigation-call__error">
          <span v-if="call.errorType" class="investigation-call__error-type">
            {{ call.errorType }}
          </span>
          <span v-if="call.errorMessage" class="investigation-call__error-message">
            {{ call.errorMessage }}
          </span>
        </div>
      </div>

      <div v-for="section in visibleSections" :key="section.key" class="investigation-call__block">
        <template v-if="section.headers.length">
          <div class="investigation-call__block-head">
            <h6>{{ section.headersTitle }} ({{ section.headers.length }})</h6>
            <button
              type="button"
              class="ds-button ds-button--ghost ds-button--sm"
              :data-qa="`investigation-call-${call.id}-${section.key}-headers-toggle`"
              @click="openHeaders[section.key] = !openHeaders[section.key]"
            >
              {{ openHeaders[section.key] ? 'Verbergen' : 'Tonen' }}
            </button>
          </div>
          <dl v-if="openHeaders[section.key]" class="investigation-call__headers">
            <template v-for="header in section.headers" :key="header.name">
              <dt>{{ header.name }}</dt>
              <dd>{{ header.value }}</dd>
            </template>
          </dl>
        </template>

        <template v-if="section.body">
          <div
            class="investigation-call__block-head"
            :class="{ 'investigation-call__block-head--body': section.headers.length }"
          >
            <h6>{{ section.bodyTitle }}{{ section.bodyMeta }}</h6>
            <span
              v-if="call.bodyFidelity"
              class="investigation-call__fidelity"
              :title="fidelityHint"
            >
              {{ call.bodyFidelity }}
            </span>
            <span v-if="call.truncated" class="ds-chip ds-chip--dot ds-chip--warning">
              Afgekapt
            </span>
            <investigation-copy-button
              :text="section.body.text"
              :data-qa="`investigation-call-${call.id}-copy-${section.key}-body`"
            />
          </div>
          <pre
            v-if="section.body.isJson"
            class="ds-code ds-code--json"
            v-html="section.body.html"
          ></pre>
          <pre v-else class="ds-code">{{ section.body.text }}</pre>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import InvestigationCopyButton from './InvestigationCopyButton.vue'
import {
  formatDuration,
  formatNumber,
  highlightJson,
  isFailedCall,
  prettyPrintBody,
} from './investigationFormat'

// One logged API call: a summary line that expands into the endpoint, the
// error if there was one, and the headers and bodies of both directions.
//
// bodyFidelity says how the body was captured — RAW is literally what went
// over the wire, SERIALIZED_DTO was re-serialised from the internal object.
const FIDELITY_HINTS = {
  RAW: 'letterlijke body',
  SERIALIZED_DTO: 'geserialiseerd object',
}

export default {
  name: 'InvestigationCall',
  components: { InvestigationCopyButton },
  props: {
    call: {
      type: Object,
      required: true,
    },
    open: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle'],
  data() {
    return { openHeaders: { request: false, response: false } }
  },
  computed: {
    failed() {
      return isFailedCall(this.call)
    },
    statusLabel() {
      if (this.call.httpStatus != null) return this.call.httpStatus
      return this.failed ? 'Mislukt' : 'OK'
    },
    methodClass() {
      return `investigation-call__method--${(this.call.method || '').toLowerCase()}`
    },
    fidelityHint() {
      return FIDELITY_HINTS[this.call.bodyFidelity] || this.call.bodyFidelity
    },
    // Both directions have the same shape, so they are built the same way and
    // rendered by one loop. A direction with neither headers nor a body — a
    // GET's request side — is dropped rather than rendered as an empty block.
    visibleSections() {
      return [
        this.buildSection('request', 'Request headers', 'Request body'),
        this.buildSection('response', 'Response headers', 'Response body'),
      ].filter((section) => section.headers.length || section.body)
    },
  },
  methods: {
    formatDuration,
    buildSection(key, headersTitle, bodyTitle) {
      const headers = this.call[`${key}Headers`] || {}
      const rawBody = this.call[`${key}Body`]
      const bytes = this.call[`${key}BodyBytes`]

      let body = null
      if (rawBody) {
        const { text, isJson } = prettyPrintBody(rawBody)
        body = { text, isJson, html: isJson ? highlightJson(text) : '' }
      }

      const meta = [bytes ? `${formatNumber(bytes)} bytes` : null, body?.isJson ? 'JSON' : null]
        .filter(Boolean)
        .join(' · ')

      return {
        key,
        headersTitle,
        bodyTitle,
        bodyMeta: meta ? ` · ${meta}` : '',
        headers: Object.keys(headers).map((name) => ({ name, value: headers[name] })),
        body,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.investigation-call {
  overflow: hidden;
  border: 1px solid var(--ds-color-border);
  border-radius: 10px;
  background: var(--ds-color-surface);

  + .investigation-call {
    margin-top: var(--ds-space-2);
  }

  &--failed {
    border-color: var(--ds-color-danger-border);
  }

  &__head {
    display: grid;
    grid-template-columns: 30px 62px 1fr 92px auto 22px;
    gap: 14px;
    align-items: center;
    width: 100%;
    padding: var(--ds-space-3) var(--ds-space-4);
    border: 0;
    background: none;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: var(--ds-color-surface-hover);
    }
  }

  &--failed > &__head {
    background: var(--ds-color-danger-bg);
  }

  &__id {
    font-family: $font-family-mono;
    font-size: var(--ds-font-size-xs);
    font-variant-numeric: tabular-nums;
    color: var(--ds-color-text-subtle);
  }

  &__method {
    padding: 3px 0;
    border-radius: var(--ds-radius-sm);
    background: var(--ds-color-border-subtle);
    color: var(--ds-color-text-muted);
    font-family: $font-family-mono;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: center;

    &--post {
      background: var(--ds-chip-info-bg);
      color: var(--ds-chip-info-text);
    }
  }

  &__operation {
    min-width: 0;

    b {
      display: block;
      overflow: hidden;
      font-size: var(--ds-font-size-base);
      font-weight: 600;
      color: var(--ds-color-text);
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    span {
      display: block;
      margin-top: 2px;
      overflow: hidden;
      font-family: $font-family-mono;
      font-size: 11.5px;
      color: var(--ds-color-text-subtle);
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &__status,
  &__flags {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--ds-space-2);
  }

  &__duration {
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    color: var(--ds-color-text-muted);
    text-align: right;
  }

  &__chevron {
    color: var(--ds-color-text-subtle);
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: var(--ds-space-4);
    border-top: 1px solid var(--ds-color-border-subtle);
    background: var(--ds-color-surface-hover);
  }

  &__block {
    min-width: 0;
  }

  &__block-head {
    display: flex;
    align-items: center;
    gap: var(--ds-space-3);
    margin-bottom: 6px;

    h6 {
      margin: 0;
      font-size: 10.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      color: var(--ds-color-text-subtle);
    }

    // The headers block sits directly above it, so the body heading needs air.
    &--body {
      margin-top: 14px;
    }
  }

  &__uri {
    padding: 10px var(--ds-space-3);
    border: 1px solid var(--ds-color-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface);
    font-family: $font-family-mono;
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--ds-color-brand);
    word-break: break-all;
  }

  &__headers {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2px 14px;
    max-height: 200px;
    padding: 10px var(--ds-space-3);
    overflow: auto;
    border: 1px solid var(--ds-color-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface);
    font-family: $font-family-mono;
    font-size: 11.5px;

    dt {
      color: var(--ds-color-text-subtle);
      white-space: nowrap;
    }

    dd {
      margin: 0;
      color: var(--ds-color-text);
      word-break: break-all;
    }
  }

  &__fidelity {
    padding: 2px 6px;
    border-radius: var(--ds-radius-sm);
    background: var(--ds-color-border-subtle);
    color: var(--ds-color-text-subtle);
    font-family: $font-family-mono;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
  }

  &__error {
    display: flex;
    flex-direction: column;
    gap: var(--ds-space-1);
    padding: 10px var(--ds-space-3);
    border: 1px solid var(--ds-color-danger-border);
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-danger-bg);
  }

  &__error-type {
    font-family: $font-family-mono;
    font-size: var(--ds-font-size-xs);
    font-weight: 700;
    color: var(--ds-color-danger-text);
  }

  &__error-message {
    font-size: var(--ds-font-size-sm);
    line-height: 1.5;
    color: var(--ds-color-text);
  }
}
</style>
